import { ratingAtom, totalCountAtom } from '@components/reviews/atoms';
import { afterNextPaint } from '@helpers/after-next-paint';
import useAnalyticsContext from '@hooks/useAnalyticsContext';
import { useTrackStorefrontImpression } from '@hooks/useTrackStorefrontImpression';
import { useAppSelector } from '@redux/hooks';
import { selectIsMobile } from '@redux/viewport/selectors';
import { TextBody } from '@tkww/orion-web-ds-text';
import classNames from 'classnames';
import { useAtomValue } from 'jotai';
import React, { type FC } from 'react';
import { GoogleIconSmall } from '../../GoogleIconSmall';
import { TKIconSmall } from '../../TKIconSmall';
import Styles from './GoogleReviewsToggleSelector.scss';

export interface OwnProps {
	showTKReviews: boolean;
	setShowTKReviews: React.Dispatch<React.SetStateAction<boolean>>;
	googleRating: number;
	googleReviewsCount: number;
	googleReviewsCountFormatted?: string | null;
}

type Props = OwnProps;

export const GoogleReviewsToggleSelector: FC<Props> = ({
	showTKReviews,
	setShowTKReviews,
	googleReviewsCount,
	googleReviewsCountFormatted,
	googleRating,
}) => {
	const impressionRef = useTrackStorefrontImpression('Google Reviews');
	const isMobile = useAppSelector((state) => selectIsMobile(state));
	const reviewsCount = useAtomValue(totalCountAtom);
	const rating = useAtomValue(ratingAtom);

	const { track } = useAnalyticsContext();

	const onClickTKReviews = () => {
		if (!showTKReviews) {
			afterNextPaint(() => {
				setShowTKReviews(true);
				track({
					event: 'Review Interaction',
					properties: {
						selection: 'Reviews Block',
						action: 'Click TK Reviews Tab',
					},
				});
			});
		}
	};
	const onClickGoogleReviews = () => {
		if (showTKReviews) {
			afterNextPaint(() => {
				setShowTKReviews(false);
				track({
					event: 'Review Interaction',
					properties: {
						selection: 'Reviews Block',
						action: 'Click Google Reviews Tab',
					},
				});
			});
		}
	};

	const hasTkReviews = reviewsCount > 0;
	const renderedGoogleReviewsCount =
		googleReviewsCountFormatted ?? googleReviewsCount;

	return (
		<div
			className={Styles.reviewTabsContainer}
			ref={impressionRef}
			data-testid="google-reviews-toggle-selector"
		>
			<button
				type="button"
				tabIndex={0}
				className={classNames(
					Styles.reviewTab,
					showTKReviews ? Styles.active : '',
				)}
				onClick={() => onClickTKReviews()}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						onClickTKReviews();
					}
				}}
				data-testid="tk-reviews-tab"
			>
				<div className={Styles.reviewTabLogo}>
					<TKIconSmall />
				</div>
				<div className={Styles.reviewTabContent}>
					<TextBody size="md" bold>
						The Knot
					</TextBody>
					<div className={Styles.reviewTabSummary}>
						{hasTkReviews && (
							<TextBody size="sm" bold>
								{rating.toFixed(1)}/5
							</TextBody>
						)}
						<TextBody size="sm" className={Styles.reviewTabRating}>
							{hasTkReviews ? '·' : ''} {reviewsCount}{' '}
							{(!isMobile || !hasTkReviews) && 'reviews'}
						</TextBody>
					</div>
				</div>
			</button>
			<button
				type="button"
				tabIndex={0}
				className={classNames(
					Styles.reviewTab,
					!showTKReviews ? Styles.active : '',
				)}
				onClick={() => onClickGoogleReviews()}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || e.key === ' ') {
						onClickGoogleReviews();
					}
				}}
			>
				<div className={Styles.reviewTabLogo}>
					<GoogleIconSmall />
				</div>
				<div className={Styles.reviewTabContent}>
					<TextBody size="md" bold>
						Google
					</TextBody>
					<div className={Styles.reviewTabSummary}>
						<TextBody size="sm" bold>
							{googleRating.toFixed(1)}/5
						</TextBody>
						<TextBody size="sm" className={Styles.reviewTabRating}>
							· {renderedGoogleReviewsCount} {!isMobile && 'reviews'}
						</TextBody>
					</div>
				</div>
			</button>
		</div>
	);
};
